/* eslint-disable complexity */
import { batch } from 'react-redux'
import qs from 'query-string'
import { hasOperationMetadata } from 'utils/card'
import { redirectTo, goTo } from 'utils/navigate'
import { get } from 'lodash-es'
import { handleUnexpectedError } from 'utils/error/error-handler'
import { constructStepUpRedirectUrl } from 'utils/risk'
import { getQueryParams, getQueryParam } from 'utils/query'
import { createReplaceCardFlowUrl, createSetPinFlowUrl } from 'utils/flow'
import { getProductQueryParam, isValidProductName } from 'utils/utils'
import { FLOW_STEPS } from '@/constants/flow-steps'
import {
  getProductSystemNameByAlias,
  applicationStatus,
  OPERATIONS,
  SUB_OPERATIONS,
  isCardShipped,
  isCardNotShipped,
  CARD_ACTIVATION_ERROR_CODES,
  NEED_MORE_DATA_CODES,
  NEED_MORE_INFO_VALUES,
} from 'universal/cfs-card'
import { INTENT_TYPES } from 'universal/constants'
import { setPageError, updateCvv, clearAutoSubmit } from '@/features/activate-card/activate-card-slice'
import { successfulFetch } from '@/features/ui/ui-slice'
import { REPLACE_CARD_REASONS } from '@/constants/card'
import {
  getCardManagementPath,
  generateCipRedirectUrl,
  GET_PP_APP,
  PP_SUMMARY,
} from 'universal/routes'

import { FLOWS } from 'universal/ucs'
import { getPayPalUrl, serverData } from 'utils/server-data'
import { isFromEnrollmentFlow } from 'utils/screens/enroll-card'

const { MANAGE, ACTIVATE_CARD } = FLOWS
const {
  ENTER_CVV_STEP,
  CARD_NOT_RECEIVED_STEP,
  NEED_MORE_DATA_STEP,
  MANUAL_REVIEW_STEP,
  DENIED_STEP,
  CONFIRM_INFO_STEP,
} = FLOW_STEPS.ACTIVATE
const { STEPUP_AUTH, NEED_MORE_DATA, MANUAL_REVIEW, DENIED } = applicationStatus
const { INVALID_CVV, MISMATCHED_CVV } = CARD_ACTIVATION_ERROR_CODES
const { APPLICATIONS } = OPERATIONS
const { ACTIVATE } = SUB_OPERATIONS
const { LOST_OR_STOLEN } = REPLACE_CARD_REASONS
const { NEED_MORE_INFO } = NEED_MORE_DATA_CODES

export const INVALID_PRODUCT_NAME = 'INVALID_PRODUCT_NAME'

export const INVALID_CVV_ERROR = 'invalidCvv'

export const PIN_INPUT_DEFAULT_LENGTH = 4

/**
 * FWC - Card and PIN has always to be set together
 * PPDC - Supports shared PIN, card could have been activated but PIN not set
 */
export function isActivateCardAllowed(card) {
  return hasOperationMetadata(card, { operation: APPLICATIONS, subOperation: ACTIVATE })
}

export function checkRUGSResponse({ applyCard, card, nextStep }) {
  const { status, resultDetails, nonce = '', links = [] } = applyCard
  const { code = '', values = [] } = resultDetails || {}

  switch (status) {
    case STEPUP_AUTH:
      const { shipmentStatus } = card?.shipmentDetails || {}
      const stepUrlQueryParams = getQueryParams()
      let additionalQueryParams = {}

      if (isCardNotShipped(shipmentStatus)) {
        additionalQueryParams = {
          page: ENTER_CVV_STEP,
        }
      }

      const stepUpUrl = constructStepUpRedirectUrl(values, MANAGE, {
        ...stepUrlQueryParams,
        ...(nonce && { nonce }),
        ...additionalQueryParams,
      })
      redirectTo(stepUpUrl)
      return true

    /**
     * PPDC US
     * - NEED_MORE_DATA || MANUAL_REVIEW (status) + NEED_MORE_INFO (code) + SSN (value)
     * PPDC DE
     * - NEED_MORE_DATA || MANUAL_REVIEW (status)
     * - Rely on link's title and id
     */
    case NEED_MORE_DATA:
    case MANUAL_REVIEW:
      const defaultOptions = {
        flow: ACTIVATE_CARD,
        additionalReturnUrlParams: nonce ? { nonce } : {},
      }
      // Compliance verification link is not returned for PPDC US
      if (links && links.length > 0) {
        const cipLink = links.find(
          (link) => link.rel === 'redirect' && link.title === 'compliance-verification',
        )
        if (cipLink && cipLink.id) {
          const processBasedCipUrl = generateCipRedirectUrl({
            ...defaultOptions,
            includeReturnUrl: !!code,
            additionalParams: {
              showLandingScreen: !code,
              showAcknowledgementScreen: !code,
            },
            pid: cipLink.id,
          })
          redirectTo(processBasedCipUrl)
          return true
        }
      }

      if (code === NEED_MORE_INFO && values.includes(NEED_MORE_INFO_VALUES.SSN)) {
        const cipRedirectUrl = generateCipRedirectUrl({
          ...defaultOptions,
        })
        redirectTo(cipRedirectUrl)
        return true
      }

      nextStep(status === NEED_MORE_DATA ? NEED_MORE_DATA_STEP : MANUAL_REVIEW_STEP)
      return true

    case DENIED:
      nextStep(DENIED_STEP)
      return true

    default:
      throw new Error(`Unhandled apply card RUGS status ${status}`)
  }
}

export function handleActivateCardError(error, options = {}) {
  const { nextStep, dispatch, fetchName } = options
  const { details } = error || {}
  const issue = get(details, '[0].issue')
  if ([INVALID_CVV, MISMATCHED_CVV].includes(issue)) {
    batch(() => {
      dispatch(successfulFetch({ fetchName }))
      dispatch(
        setPageError({
          page: 'enterCvv',
          error: 'invalidCvv',
        }),
      )
      // Clear the stored CVV from Redux state for cleanup
      dispatch(updateCvv(null))
      // Clear autoSubmit flag after error to prevent duplicate submissions
      dispatch(clearAutoSubmit())
    })
    nextStep(ENTER_CVV_STEP)
  } else {
    handleUnexpectedError(error, options)
  }
}

export function getFlowInputParams() {
  let productName = getQueryParam('productName')
  let directEntry = getQueryParam('directEntry')
  return {
    productName: isValidProductName(ACTIVATE_CARD, productName)
      ? getProductSystemNameByAlias(productName)
      : null,
    directEntry: Boolean(directEntry),
  }
}

export const isValidCvv = (cvv) => {
  return /^[0-9]{3}$/.test(cvv)
}

export const cvvInputMaskOptions = {
  mask: 'CVV',
  blocks: {
    CVV: {
      mask: '000',
    },
  },
  lazy: true,
}

export const goToReplaceCardFlow = (card) => {
  if (!card) return

  const { productName = '' } = card

  if (!productName) return

  const returnUriQueryString = qs.stringify({
    productName: getProductQueryParam(productName),
    page: CARD_NOT_RECEIVED_STEP,
    ...qs.parse(window.location.search),
  })

  const returnUri = window.location.pathname + `?${returnUriQueryString}`

  const replaceCardFlowUrl = createReplaceCardFlowUrl(card, LOST_OR_STOLEN, returnUri)

  if (!replaceCardFlowUrl) {
    return
  }

  goTo(replaceCardFlowUrl)
}

const ENTER_CVV_STEP_INFO = {
  firstStep: 1,
  firstStepSwitchName: ENTER_CVV_STEP,
}

const ENTER_CVV_AS_FIRST_STEP = {
  ...ENTER_CVV_STEP_INFO,
  hideStepsBeforeFirst: true,
}

const CARD_NOT_RECEIVED_AS_FIRST_STEP = {
  firstStep: 2,
  firstStepSwitchName: CARD_NOT_RECEIVED_STEP,
  hideStepsBeforeFirst: Boolean(getQueryParam('directEntry')),
}

const CONFIRM_INFO_STEP_AS_FIRST_STEP = {
  firstStep: 3,
  firstStepSwitchName: CONFIRM_INFO_STEP,
  hideStepsBeforeFirst: true,
}

export const overrideActivateFlowFirstStepByCardDetails = (card, options = {}) => {
  const page = getQueryParam('page')
  const { shipmentDetails: { shipmentStatus = '' } = {}, autoSubmit = false } = card || {}
  const { activationSucceededWithAutoSubmit = false } = options

  if (!card) {
    return { firstStep: -1 }
  }

  if (autoSubmit) {
    return CONFIRM_INFO_STEP_AS_FIRST_STEP
  }

  // After autoSubmit success keep first step at CONFIRM_INFO so stepper does not jump back to EnterCvv
  if (activationSucceededWithAutoSubmit) {
    return CONFIRM_INFO_STEP_AS_FIRST_STEP
  }

  if (page) {
    switch (page) {
      case CARD_NOT_RECEIVED_STEP:
        // "Didn't get your card?" webview case
        // URL: myaccount/debitcards/activate?productName=CONSUMER_DEBIT_CARD&page=cardNotReceived&directEntry=true&webviewCallback=onReplaceCardComplete&action_type=primary&locale.x=en_US
        if (isCardShipped(shipmentStatus)) {
          return { ...CARD_NOT_RECEIVED_AS_FIRST_STEP, terminalSteps: [ENTER_CVV_STEP] }
        }
        break
      case ENTER_CVV_STEP:
        // To handle after stepup auth has been resolved
        if (isCardNotShipped(shipmentStatus)) {
          return ENTER_CVV_STEP_INFO
        }
        break
      default:
        break
    }
    return ENTER_CVV_AS_FIRST_STEP
  }

  return ENTER_CVV_AS_FIRST_STEP
}

export function getPaypalAppLink() {
  const country = serverData.locality?.country?.toLowerCase()
  return getPayPalUrl(`${country}${GET_PP_APP}`)
}

export const goToSetPinFlow = (card, options = {}) => {
  if (!card) return

  const { productName = '' } = card
  const { flowName, hidePinSuccessPage } = options

  if (!productName) return

  const returnUri = getCardManagementPath(card)
  let cancelUri = getQueryParam('returnUri') || PP_SUMMARY

  const additionalParams = { returnUri, cancelUri }
  if (isFromEnrollmentFlow(flowName)) {
    additionalParams.intent = INTENT_TYPES.ENROLL
    delete additionalParams.returnUri
  }

  if (getQueryParam('skipPinRedirect')) {
    additionalParams.skipPinRedirect = true
  }

  if (hidePinSuccessPage) {
    additionalParams.hidePinSuccess = true
  }

  const setPinFlowUrl = createSetPinFlowUrl(card, additionalParams)

  if (!setPinFlowUrl) {
    return
  }

  goTo(setPinFlowUrl)
}

export const isActivateFlowInFirstStep = (stepperRef) => {
  const { getCurrentStepName } = stepperRef.current || {}

  if (getCurrentStepName) {
    const currentStepName = getCurrentStepName()
    return [ENTER_CVV_STEP].includes(currentStepName)
  }

  return false
}
